import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import authStore from '../store/authStore';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/';
  const { login, loading, error } = authStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [localError, setLocalError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    if (!email || !password) { setLocalError('Compila tutti i campi'); return; }
    try {
      await login(email, password);
      navigate(redirectTo);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail) {
        setLocalError(detail);
      } else if (err.code === 'ERR_NETWORK' || err.message?.includes('Network')) {
        setLocalError('Errore di connessione. Riprova tra qualche secondo.');
      } else {
        setLocalError('Credenziali non valide');
      }
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="https://customer-assets.emergentagent.com/job_makehome-frontend/artifacts/pnxu5fqe_6.png" alt="MakeHome 360" className="h-12 w-auto mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white">Bentornato</h1>
          <p className="text-slate-400 text-sm mt-1">Accedi al tuo account MakeHome 360</p>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-xl">
          {(localError || error) && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-xl mb-4" data-testid="login-error">
              {localError || error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email" value={email} onChange={e => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#10B981] focus:border-transparent outline-none transition"
                  placeholder="La tua email"
                  data-testid="login-email"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#10B981] focus:border-transparent outline-none transition"
                  placeholder="La tua password"
                  data-testid="login-password"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} className="w-full py-3 bg-[#10B981] hover:bg-[#059669] text-white font-semibold rounded-xl transition disabled:opacity-50" data-testid="login-submit">
              {loading ? 'Accesso...' : 'Accedi'}
            </button>
          </form>

          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
            <div className="relative flex justify-center"><span className="bg-white px-3 text-sm text-gray-400">oppure</span></div>
          </div>

          <button onClick={handleGoogleLogin} className="w-full flex items-center justify-center gap-3 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition" data-testid="google-login-btn">
            <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            <span className="text-sm font-medium text-gray-700">Accedi con Google</span>
          </button>

          <p className="text-center text-sm text-gray-500 mt-5">
            Non hai un account? <Link to="/register" className="text-[#10B981] font-semibold hover:underline" data-testid="register-link">Registrati</Link>
          </p>
          <p className="text-center text-sm text-gray-400 mt-2">
            <Link to="/forgot-password" className="hover:text-[#10B981] hover:underline transition" data-testid="forgot-password-link">Password dimenticata?</Link>
          </p>
        </div>
        <p className="text-center mt-4">
          <Link to="/admin/login" className="text-xs text-slate-500 hover:text-slate-300 transition" data-testid="admin-access-link">Accesso Admin</Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
